import dayjs, { Dayjs } from 'dayjs';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { formatDateYmd } from './dateFormat';

const dayPickerFormat = parseFormat(JA_JP.format);
const monthPickerFormat = parseFormat(JA_JP.formatMonth);

/**
 * Convert RA2NDatePicker value (和暦 or Y-m-d) to Y-m-d for API. Returns null if empty/invalid.
 */
export function dateValueToIso(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const d = parseDate(value.trim(), dayPickerFormat);
  if (Number.isNaN(d.getTime())) return null;
  return formatDateYmd(d);
}

export function normalizeOptionalDate(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

/**
 * Convert RA2NDatePicker month-picker value (和暦 yyyy年mm月 or Y-m) to Y-m for API.
 * Returns null if empty/invalid.
 */
export function monthValueToIso(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  // Already ISO 'YYYY-MM'
  if (/^\d{4}-\d{2}$/.test(trimmed)) return trimmed;
  const d = parseDate(trimmed, monthPickerFormat);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * Format only the year in Japanese Era (和暦) for date picker year panel
 * @param value - Dayjs object or date-like (from picker generateConfig)
 * @returns Japanese era year string (e.g., "令和6年")
 */
export const convGtJYear = (value: Dayjs | string | Date | null | undefined): string => {
  if (value == null) return '';

  let dayjsValue: Dayjs;
  if (typeof value === 'string') {
    dayjsValue = dayjs(value);
  } else if (value instanceof Date) {
    dayjsValue = dayjs(value);
  } else {
    dayjsValue = value as Dayjs;
  }

  if (!('isValid' in dayjsValue) || !dayjsValue.isValid()) return '';

  const date = dayjsValue.toDate();
  const parts = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', {
    era: 'long',
    year: 'numeric',
  }).formatToParts(date);

  let era = '';
  let year = '';
  for (const part of parts) {
    if (part.type === 'era') era = part.value;
    if (part.type === 'year') year = part.value === '元' ? '01' : part.value;
  }
  return era && year ? `${era}${year}年` : '';
};

/**
 * Convert a date to Japanese Era format (和暦)
 * @param value - Dayjs object or date string
 * @returns Japanese era formatted date string (e.g., "令和6年1月27日")
 */
export const convGtJDate = (value: Dayjs | string | null): string => {
  if (!value) return '';

  let dayjsValue: Dayjs;
  if (typeof value === 'string') {
    dayjsValue = dayjs(value);
  } else {
    dayjsValue = value;
  }

  if (!dayjsValue.isValid()) return '';

  const date = dayjsValue.toDate();
  const parts = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', {
    era: 'long',
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  let result = '';
  for (const part of parts) {
    if (part.type === 'literal') continue;
    result += part.type === 'year' && part.value === '元' ? '01' : part.value;
    if (part.type === 'year') result += '年';
    if (part.type === 'month') result += '月';
    if (part.type === 'day') result += '日';
  }
  return result;
};

/**
 * Convert a timestamp to 和暦 date + 24-hour clock — `元号00年00月00日 HH:mm`.
 *
 * The date half is 和暦 like every other date on the portal; the clock stays 24-hour because there is no 和暦
 * form of a time, and it is the part that says whether two notices were the same incident. A date outside the
 * era table falls back to 西暦 rather than showing a bare time with no day attached to it.
 */
export const convGtJDateTime = (value: Dayjs | string | null | undefined): string => {
  if (!value) return '';

  const dayjsValue: Dayjs = typeof value === 'string' ? dayjs(value) : (value as Dayjs);
  if (!dayjsValue.isValid()) return '';

  const wareki = convGtJDate(dayjsValue);
  return `${wareki || dayjsValue.format('YYYY/MM/DD')} ${dayjsValue.format('HH:mm')}`;
};

/**
 * Convert a date to short Japanese Era format (和暦 short) — era initial + YY.MM.DD.
 * Eg: 1988-05-26 → "H63.05.26"; 2024-04-01 → "R06.04.01"; 2020-05-26 → "R02.05.26".
 * Returns empty string for invalid / null / '0000-00-00'.
 */
export const convGtJDateShort = (value: Dayjs | string | null | undefined): string => {
  if (!value || value === '0000-00-00') return '';

  const dayjsValue: Dayjs = typeof value === 'string' ? dayjs(value) : (value as Dayjs);
  if (!dayjsValue.isValid()) return '';

  const parts = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', {
    era: 'narrow',
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(dayjsValue.toDate());

  let era = '';
  let year = '';
  let month = '';
  let day = '';
  for (const part of parts) {
    if (part.type === 'era') era = part.value;
    else if (part.type === 'year') year = part.value === '元' ? '01' : part.value.padStart(2, '0');
    else if (part.type === 'month') month = part.value.padStart(2, '0');
    else if (part.type === 'day') day = part.value.padStart(2, '0');
  }
  if (!era || !year || !month || !day) return '';
  return `${era}${year}.${month}.${day}`;
};

/**
 * Convert a date to Japanese Era format (和暦) - Month only
 * @param value - Dayjs object or date string
 * @returns Japanese era formatted month string (e.g., "令和06年04月")
 */
export const convGtJMonth = (value: Dayjs | string | null): string => {
  if (!value) return '';

  let dayjsValue: Dayjs;
  if (typeof value === 'string') {
    dayjsValue = dayjs(value);
  } else {
    dayjsValue = value;
  }

  if (!dayjsValue.isValid()) return '';

  const date = dayjsValue.toDate();
  const parts = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', {
    era: 'long',
    year: '2-digit',
    month: '2-digit',
  }).formatToParts(date);

  let result = '';
  for (const part of parts) {
    if (part.type === 'literal') continue;
    if (part.type === 'year') {
      const yearStr = part.value === '元' ? '01' : part.value.padStart(2, '0');
      result += `${yearStr}年`;
    } else if (part.type === 'month') {
      result += `${part.value.padStart(2, '0')}月`;
    } else {
      result += part.value;
    }
  }
  return result;
};

/**
 * Convert Japanese era date string back to ISO format
 * @param japaneseDateStr - Japanese era formatted date (e.g., "令和6年1月27日")
 * @returns ISO date string (YYYY-MM-DD)
 */

export const japaneseDateToISO = (japaneseDateStr: string): string => {
  if (!japaneseDateStr) return '';

  const eraMap: Record<string, number> = {
    令和: 2018,
    平成: 1988,
    昭和: 1925,
    大正: 1911,
    明治: 1867,
  };

  const match = japaneseDateStr.match(/(令和|平成|昭和|大正|明治)(元|\d+)年(\d{1,2})月(\d{1,2})日/);

  if (!match) return '';

  const [, eraName, eraYearRaw, monthRaw, dayRaw] = match;

  const eraYear = eraYearRaw === '元' ? 1 : Number(eraYearRaw);
  const westernYear = eraMap[eraName] + eraYear;

  const date = dayjs(`${westernYear}-${monthRaw.padStart(2, '0')}-${dayRaw.padStart(2, '0')}`);

  if (!date.isValid()) return '';

  return date.format('YYYY-MM-DD');
};

/**
 * Convert wareki month string (e.g. 令和08年04月) to YYYYMM integer (e.g. 202604)
 */
export const warekiMonthToYearmonth = (val: string): number | undefined => {
  const digits = val.match(/\d+/g);
  if (!digits || digits.length < 2) return undefined;
  if (digits[0].length === 4) {
    return parseInt(`${digits[0]}${digits[1].padStart(2, '0')}`, 10);
  }
  const eraMatch = val.match(/(明治|大正|昭和|平成|令和)(\d+)/);
  if (!eraMatch) return undefined;
  const eraName = eraMatch[1];
  const eraYear = parseInt(eraMatch[2], 10);
  const eraBaseMap: Record<string, number> = { '明治': 1867, '大正': 1911, '昭和': 1925, '平成': 1988, '令和': 2018 };
  const gregorianYear = (eraBaseMap[eraName] ?? 2018) + eraYear;
  const month = parseInt(digits[digits.length - 1], 10);
  return gregorianYear * 100 + month;
};

/**
 * Convert YYYYMM integer (e.g. 202604) to ISO date string (e.g. 2026-04-01) for RA2NDatePicker
 */
export const yearmonthToDateStr = (ym: number | undefined | null): string => {
  if (!ym) return '';
  const s = String(ym);
  if (s.length !== 6) return '';
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-01`;
};

/**
 * Convert YYYY-MM string to Japanese era month format (e.g. "2026-04" → "令和08年04月").
 * Safe to call with partial ISO dates like "2026-04".
 */
export function isoMonthToJaWareki(yyyymm: string): string {
  if (!yyyymm) return '';
  const dateStr = /^\d{4}-\d{2}$/.test(yyyymm) ? `${yyyymm}-01` : yyyymm;
  return convGtJMonth(dateStr);
}

/**
 * Normalize a yearmonth value to canonical `YYYYMM` (6-digit) form, or `null` if invalid.
 * Accepts: `YYYYMM`, `YYYY-MM`, or Japanese wareki (`令和08年04月`).
 */
export function normalizeYearmonth(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return null;

  if (/^\d{6}$/.test(trimmed)) {
    const month = Number(trimmed.substring(4, 6));
    return month >= 1 && month <= 12 ? trimmed : null;
  }

  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    const month = Number(trimmed.substring(5, 7));
    return month >= 1 && month <= 12
      ? `${trimmed.substring(0, 4)}${trimmed.substring(5, 7)}`
      : null;
  }

  if (!/^(令和|平成|昭和|大正|明治)(元|\d{1,2})年\d{1,2}月$/.test(trimmed)) {
    return null;
  }

  const yearmonth = warekiMonthToYearmonth(trimmed);
  if (!yearmonth) return null;
  const payload = String(yearmonth);
  if (!/^\d{6}$/.test(payload)) return null;
  const month = Number(payload.substring(4, 6));
  return month >= 1 && month <= 12 ? payload : null;
}

/**
 * Parse a yearmonth URL param to YYYY-MM.
 * Accepts both Japanese era format ("令和08年04月") and ISO ("YYYY-MM") for backward compatibility.
 */
export function parseYearmonthParam(param: string): string {
  if (!param) return '';
  if (/^\d{4}-\d{2}$/.test(param)) return param;
  const ym = warekiMonthToYearmonth(param);
  if (!ym) return param;
  const s = String(ym);
  if (s.length !== 6) return param;
  return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
}

/**
 * Format a date period for display in tables/modals
 * Displays: {available_from} - {available_to}
 * If available_to is null or '2100-12-31'/'2100-31-12', displays: {available_from} -
 *
 * @param availableFrom - Start date string (YYYY-MM-DD format or Dayjs)
 * @param availableTo - End date string (YYYY-MM-DD format or Dayjs), can be null
 * @returns Formatted period string (e.g., "令和06年01月16日 - 令和06年12月31日" or "令和06年01月16日 -")
 */
export const formatPeriodDisplay = (
  availableFrom: string | null | undefined,
  availableTo: string | null | undefined
): string => {
  const fromStr = convGtJDate(availableFrom ?? '');
  if (!fromStr) return '';

  const isIndefinite = !availableTo || availableTo === '2100-12-31' || availableTo === '2100-31-12';

  if (isIndefinite) {
    return `${fromStr} -`;
  }

  const toStr = convGtJDate(availableTo);
  return `${fromStr} - ${toStr}`;
};

/**
 * Format an end date for display, hiding if it's an indefinite date (2100-12-31 or 2100-31-12)
 * Used for 適用終了日 (applicable end date) - should be hidden when there's no specific end date
 *
 * @param dateStr - Date string (YYYY-MM-DD format) or Dayjs object
 * @returns Formatted date string or empty string if indefinite
 */
export const formatEndDateOrHide = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';

  const isIndefinite = dateStr === '2100-12-31' || dateStr === '2100-31-12';
  if (isIndefinite) {
    return '';
  }

  return convGtJDate(dateStr);
};
